// Turbo head pruner.
//
// Background: Turbo Drive's `mergeHead` is asymmetric — it APPENDS
// new <link rel="stylesheet"> elements from the destination page
// into the current head, but never REMOVES stylesheets that were
// only on the source page. The author's stated rationale is to
// avoid FOUC during the swap. The trade-off is that stylesheets
// accumulate across navigations.
//
// On this codebase this is a real bug: the new app-shell pages
// (Tailwind only) and the legacy pages (`content_for :legacy_css`
// → Bootstrap) coexist behind the same Turbo Drive session. As
// soon as the user visits ANY legacy page, Bootstrap's stylesheet
// gets injected into the head and then sticks there for the rest
// of the session — overriding the new Tailwind chrome on every
// page including /dashboard.
//
// IMPLEMENTATION NOTES:
//
// 1) We can't read the new page's head via
//    `event.detail.newBody.ownerDocument.head` in
//    `turbo:before-render` — empirically Turbo detaches the new
//    body from its parsed document, leaving an empty/stale head
//    accessible by that path. Instead we capture the raw response
//    in `turbo:before-fetch-response`, clone it, and start parsing
//    asynchronously.
//
// 2) `turbo:before-render` fires synchronously right after fetch
//    resolution — the async response.text() likely hasn't completed
//    by then. We use Turbo's own pause/resume mechanism: call
//    `event.preventDefault()` to halt rendering, await the parse,
//    do the prune, then call `event.detail.resume()` to continue.
//    The pause is short (a few ms for typical pages) and Turbo
//    explicitly supports this pattern.
//
// Selectivity rules for pruning:
//   - Only operate on <link rel="stylesheet"> elements.
//   - Compare by absolute `href` (resolved URL with digest).
//   - Skip elements marked `data-turbo-permanent`.

let pendingParse = null

document.addEventListener("turbo:before-fetch-response", (event) => {
  const fetchResponse = event.detail && event.detail.fetchResponse
  const response = fetchResponse && fetchResponse.response
  if (!response) return

  const contentType = response.headers.get("content-type") || ""
  if (!contentType.includes("text/html")) return

  // Clone immediately so Turbo's own consumption isn't disrupted,
  // then start the parse. Store the promise so the next
  // `turbo:before-render` can await it.
  pendingParse = response
    .clone()
    .text()
    .then((text) => {
      const doc = new DOMParser().parseFromString(text, "text/html")
      return new Set(
        Array.from(doc.head.querySelectorAll('link[rel="stylesheet"]')).map(
          (link) => link.href
        )
      )
    })
    .catch(() => null)
})

document.addEventListener("turbo:before-render", (event) => {
  // Only attempt to prune when we have a pending parse from the
  // current visit. Restoration visits (back/forward cache) and
  // any other render path that didn't go through a fetch should
  // not block rendering.
  if (!pendingParse) return

  const parsePromise = pendingParse
  pendingParse = null

  // Pause Turbo's render until the parse completes.
  event.preventDefault()
  const resume = event.detail && event.detail.resume

  parsePromise.then((destHrefs) => {
    try {
      if (destHrefs) {
        const liveLinks = Array.from(
          document.head.querySelectorAll('link[rel="stylesheet"]')
        )
        liveLinks.forEach((link) => {
          if (link.hasAttribute("data-turbo-permanent")) return
          if (destHrefs.has(link.href)) return
          link.remove()
        })
      }
    } finally {
      // Always resume — even if pruning threw, we don't want to
      // strand Turbo mid-render and leave the user on a stale page.
      if (typeof resume === "function") resume()
    }
  })
})
